import { routes } from "@/routes/utils";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  FIRST_NAME,
  LAST_NAME,
  EMAIL_ID,
  MOBILE_NO,
  EMP_ID,
  ADDRESS,
  STATE,
  CITY,
  ZIPCODE,
  TEMP_PASSWORD,
  COUNTRY,
  FORCE_PASSWORD,
} from "./consts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  getRequiredFields,
  removeEmptyFields,
  roles,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { schema } from "./validations";
import { useEffect, useMemo, useState } from "react";
import {
  useAddEmployeeMutation,
  useGetCityListByState,
  useGetEmployeeById,
  useGetStatesListByCountry,
  useUpdateEmployeeMutation,
} from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "@/components";

const useAddEdit = () => {
  const { id, action } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [countryCode, setCountryCode] = useState<string>("USA");
  const [isPassword, setIsPassword] = useState<boolean>(true);
  const isView = action?.toLowerCase() == "view";
  const isEdit = action?.toLowerCase() == "edit";
  const form = useForm<any>({
    defaultValues: {
      [FIRST_NAME]: "",
      [LAST_NAME]: "",
      [MOBILE_NO]: "",
      [EMAIL_ID]: "",
      [EMP_ID]: "",
      [TEMP_PASSWORD]: "",
      [FORCE_PASSWORD]: false,
      [ADDRESS]: "",
      [STATE]: "",
      [COUNTRY]: "1",
      [CITY]: "",
      [ZIPCODE]: "",
    },
    resolver: zodResolver(schema),
  });

  const [keys, setKeys] = useState<any>({
    state: Math.random() * 3,
    city: Math.random() * 3,
    community: Math.random() * 3,
    role: Math.random() * 3,
  });

  const requiredFields = getRequiredFields(schema);

  const onBackClick = () => {
    navigate(`/${routes.admin}/${routes.employeeMgmt}`);
  };

  const title = useMemo(
    () => (id ? `${form.watch(FIRST_NAME)}'s Details` : "Add Employee"),
    [form.watch(FIRST_NAME)]
  );

  const breadcrumbData = [
    {
      link: `/${routes.admin}/${routes.employeeMgmt}`,
      name: `Employee Management`,
    },
    {
      link: "",
      name: title,
    },
  ];

  const { data: employeeDetails, isLoading: isLoadingEmployeeDetails } =
    useGetEmployeeById({
      id: id as string,
      queryParams: {
        enabled: !!id,
      },
    });

  const { data: stateList, isLoading: isLoadingStates } =
    useGetStatesListByCountry({
      countryId: "1",
    });

  const { data: cityList, isLoading: isLoadingCity } = useGetCityListByState({
    stateId: form.watch(STATE),
    queryParams: {
      enabled: !!form.watch(STATE),
    },
  });

  useEffect(() => {
    if (employeeDetails?.data) {
      form.reset({
        [FIRST_NAME]: employeeDetails?.data?.[FIRST_NAME]?.toString() || "",
        [LAST_NAME]: employeeDetails?.data?.[LAST_NAME]?.toString() || "",
        [EMP_ID]: employeeDetails?.data?.[EMP_ID]?.toString() || "",
        [TEMP_PASSWORD]:
          employeeDetails?.data?.[TEMP_PASSWORD]?.toString() || "",
        [FORCE_PASSWORD]: employeeDetails?.data?.[FORCE_PASSWORD] || false,
        [MOBILE_NO]: employeeDetails?.data?.[MOBILE_NO]?.toString() || "",
        [EMAIL_ID]: employeeDetails?.data?.[EMAIL_ID]?.toString() || "",
        [ADDRESS]: employeeDetails?.data?.[ADDRESS]?.toString() || "",
        [STATE]: employeeDetails?.data?.[STATE]?.toString() || "",
        [COUNTRY]: employeeDetails?.data?.[COUNTRY]?.toString() || "",
        [CITY]: employeeDetails?.data?.[CITY]?.toString() || "",
        [ZIPCODE]: employeeDetails?.data?.[ZIPCODE]?.toString() || "",
      });
      setKeys({
        state: Math.random() * 2,
        city: Math.random() * 2,
        community: Math.random() * 2,
        role: Math.random() * 2,
      });
    }
  }, [employeeDetails]);

  const { mutate: addEmployee, isPending: isAddEmployeePending } =
    useAddEmployeeMutation({
      onError: (error?: any) => {
        toast({
          message: error?.response?.data?.errors?.length ? error?.response?.data?.errors?.join(', ') :
            typeof error?.response?.data?.message === "string"
              ? error?.response?.data?.message
              : "Something went wrong",
          type: "error",
        });
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["users"],
        });
        onBackClick();
        toast({ message: "Employee Added Successfully" });
      },
    });

  const { mutate: updateEmployee } = useUpdateEmployeeMutation({
    onError: (error?: any) => {
      toast({
        message:
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong",
        type: "error",
      });
      return error?.response?.data?.message;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["users"],
      });
      onBackClick();
      toast({ message: "Employee Updated Successfully" });
    },
  });

  const onSubmit = (data: any) => {
    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(_data);

    if (id) {
      updateEmployee({
        data: { ..._data, userType: roles.employee.value },
        id,
      });
    } else {
      delete _data[FORCE_PASSWORD];

      if (
        !_data[TEMP_PASSWORD] ||
        (_data[TEMP_PASSWORD] as string)?.length < 4
      ) {
        form.setError(TEMP_PASSWORD, {
          message: "Minimum length of password is 4.",
        });
        return;
      }

      addEmployee({
        data: { ..._data, userType: roles.employee.value },
      });
    }
  };

  const onPhoneCountryChange = (_: any, country: any) => {
    const code =
      country?.countryCode == "us"
        ? "USA"
        : country?.countryCode == "in"
          ? "IND"
          : country?.countryCode;
    if (code != countryCode) {
      setCountryCode(code);
      form.setValue(STATE, "");
      form.setValue(CITY, "");
      form.setValue(ZIPCODE, "");
    }
  };

  const onEyeIconClick = () => {
    setIsPassword(!isPassword);
  };

  const Icon = isPassword ? Eye : EyeOff;

  return {
    title,
    onBackClick,
    breadcrumbData,
    isView,
    form,
    requiredFields,
    onSubmit,
    keys,
    onPhoneCountryChange,
    countryCode,
    onCancel: onBackClick,
    isAddEmployeePending,
    isLoadingEmployeeDetails,
    isLoadingStates,
    stateList,
    isLoadingCity,
    cityList,
    isPassword,
    onEyeIconClick,
    Icon,
    isEdit,
    id,
  };
};

export default useAddEdit;
